const CACHE_VERSION = 1;
const CACHE_KEY_PREFIX = 'mt_prefs_v';

interface PreferenceCacheEntry {
  value: any;
  timestamp: number;
}

interface PreferenceCache {
  version: number;
  userId: string;
  preferences: Record<string, PreferenceCacheEntry>;
}

export const preferencesCache = {
  /**
   * Get cache key for current user
   */
  getCacheKey(userId: string): string {
    return `${CACHE_KEY_PREFIX}${CACHE_VERSION}_${userId}`;
  },

  /**
   * Initialize cache for user (called on login or mount)
   * Returns null if cache is invalid or doesn't exist
   */
  initializeCache(userId: string): PreferenceCache | null {
    try {
      const key = this.getCacheKey(userId);
      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const parsed = JSON.parse(cached) as PreferenceCache;

      // Validate cache version and user ID
      if (parsed.version !== CACHE_VERSION || parsed.userId !== userId) {
        localStorage.removeItem(key);
        return null;
      }

      return parsed;
    } catch (error) {
      console.warn('Failed to initialize preference cache:', error);
      return null;
    }
  },

  /**
   * Get single preference from cache
   */
  get(userId: string, key: string): any | undefined {
    try {
      const cache = this.initializeCache(userId);
      const entry = cache?.preferences[key];
      return entry?.value;
    } catch (error) {
      console.warn('Failed to get preference from cache:', error);
      return undefined;
    }
  },

  /**
   * Set single preference in cache (called after successful API save)
   */
  set(userId: string, key: string, value: any): void {
    try {
      const cacheKey = this.getCacheKey(userId);
      let cache = this.initializeCache(userId);

      if (!cache) {
        cache = {
          version: CACHE_VERSION,
          userId,
          preferences: {},
        };
      }

      cache.preferences[key] = {
        value,
        timestamp: Date.now(),
      };

      localStorage.setItem(cacheKey, JSON.stringify(cache));
    } catch (error) {
      console.warn('Failed to set preference in cache:', error);
    }
  },

  /**
   * Set multiple preferences (batch update)
   */
  setMany(userId: string, preferences: Record<string, any>): void {
    try {
      const cacheKey = this.getCacheKey(userId);
      let cache = this.initializeCache(userId);

      if (!cache) {
        cache = {
          version: CACHE_VERSION,
          userId,
          preferences: {},
        };
      }

      const timestamp = Date.now();
      for (const [key, value] of Object.entries(preferences)) {
        cache.preferences[key] = { value, timestamp };
      }

      localStorage.setItem(cacheKey, JSON.stringify(cache));
    } catch (error) {
      console.warn('Failed to set preferences in cache:', error);
    }
  },

  /**
   * Clear cache for user (called on logout)
   */
  clear(userId: string): void {
    try {
      const key = this.getCacheKey(userId);
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('Failed to clear preference cache:', error);
    }
  },

  /**
   * Check if localStorage is available
   */
  isAvailable(): boolean {
    try {
      const test = '__mt_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  },
};
